import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../services/api';
import { Visit } from '../types';
import { Plus, Calendar, Clock, Mail, MapPin, Search, Navigation, Route } from 'lucide-react';
import { PlanVisitsModal } from '../components/visits/PlanVisitsModal';
import { VisitDetailModal } from '../components/visits/VisitDetailModal';
import { VisitFollowupEmailModal } from '../components/visits/VisitFollowupEmailModal';
import { useAuth } from '../contexts/AuthContext';
import { formatDateShort, formatTime } from '../utils/datetime';
import {
  getOpenVisitId,
  setOpenVisitId,
} from '../utils/activeVisitDraft';

const PAGE_SIZE = 40;

function mergeVisits(existing: Visit[], incoming: Visit[]): Visit[] {
  const seen = new Set(existing.map((v) => v.id));
  const unique = incoming.filter((v) => !seen.has(v.id));
  return [...existing, ...unique];
}

const Visits: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [emailVisitId, setEmailVisitId] = useState<string | null>(null);

  useEffect(() => {
    void fetchVisits(1, false);
  }, []);

  const handleVisitClick = async (visitId: string) => {
    try {
      const res = await api.get(`/visits/${visitId}`);
      setSelectedVisit(res.data);
      setShowDetailModal(true);
      if (user?.id) setOpenVisitId(user.id, visitId);
    } catch (err) {
      console.error('Failed to fetch visit details:', err);
      toast.error('Failed to load visit details');
      if (user?.id) setOpenVisitId(user.id, null);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    const openId = getOpenVisitId(user.id);
    if (!openId) return;
    void handleVisitClick(openId);
    // Restore once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchVisits = async (pageNum: number, append: boolean) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const response = await api.get(`/visits?page=${pageNum}&limit=${PAGE_SIZE}`);
      const pageData: Visit[] = response.data.data || [];
      setTotal(response.data.total || 0);
      setPage(pageNum);
      setVisits((prev) => (append ? mergeVisits(prev, pageData) : pageData));
    } catch (error) {
      console.error('Failed to fetch visits:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (loadingMore || visits.length >= total) return;
    void fetchVisits(page + 1, true);
  };

  const hasMore = visits.length < total;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Visits</h1>
        <p className="text-gray-500">Track and manage store visits</p>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link to="/visits/new">
          <div className="p-8 bg-gradient-to-br from-brand-50 to-brand-100 border-2 border-brand-200 rounded-2xl hover:shadow-2xl transition-all duration-300 cursor-pointer hover:scale-105 hover:-translate-y-1 hover:border-brand-400">
            <Plus size={48} className="text-brand-600 mb-4 mx-auto" />
            <h3 className="text-xl font-black text-gray-900 text-center">New Visit</h3>
            <p className="text-sm text-gray-600 text-center mt-2">Log a visit to any PoS</p>
          </div>
        </Link>

        <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
        <button onClick={() => setShowPlanModal(true)} className="w-full text-left">
          <div className="p-8 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-2xl hover:shadow-2xl transition-all duration-300 cursor-pointer hover:scale-105 hover:-translate-y-1 hover:border-purple-400">
            <Navigation size={48} className="text-purple-600 mb-4 mx-auto" />
            <h3 className="text-xl font-black text-gray-900 text-center">Plan Visits</h3>
            <p className="text-sm text-gray-600 text-center mt-2">Optimize your route</p>
          </div>
        </button>
        
          <button onClick={() => navigate('/routes')} className="w-full text-left">
            <div className="p-8 bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-200 rounded-2xl hover:shadow-2xl transition-all duration-300 cursor-pointer hover:scale-105 hover:-translate-y-1 hover:border-indigo-400">
              <Route size={48} className="text-indigo-600 mb-4 mx-auto" />
              <h3 className="text-xl font-black text-gray-900 text-center">My Routes</h3>
              <p className="text-sm text-gray-600 text-center mt-2">View saved routes</p>
            </div>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={20} className="text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 sm:text-sm shadow-sm"
          placeholder="Search visits by PoS..."
        />
      </div>

      {/* Recent Visits */}
      <div>
        <div className="flex items-baseline justify-between mb-4 gap-2">
          <h2 className="text-xl font-bold">Recent Visits</h2>
          {!loading && total > 0 && (
            <p className="text-sm text-gray-500 whitespace-nowrap">
              Loaded {visits.length} of {total}
            </p>
          )}
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-24 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="bg-white shadow-sm border border-gray-100 rounded-xl overflow-hidden">
              <ul className="divide-y divide-gray-100">
                {visits.map((visit) => (
                  <li 
                    key={visit.id} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleVisitClick(visit.id)}
                  >
                    <div className="px-6 py-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {visit.pos_name}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                            visit.store_type === 'supermarket' ? 'bg-blue-50 text-blue-700' :
                            visit.store_type === 'spaeti' ? 'bg-purple-50 text-purple-700' :
                            'bg-gray-50 text-gray-700'
                          }`}>
                            {visit.store_type}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                            visit.visit_type === 'first_time' ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {visit.visit_type === 'first_time' ? 'First Visit' : 'Revisit'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {formatDateShort(visit.visit_date)}
                        </div>
                      </div>
                      
                      <div className="flex items-start justify-between">
                        <div className="text-sm text-gray-600 line-clamp-2">
                          {visit.notes ? `"${visit.notes}"` : <span className="text-gray-400 italic">No notes recorded</span>}
                        </div>
                        <div className="ml-4 flex items-center text-xs text-gray-400 whitespace-nowrap">
                          <Clock size={14} className="mr-1" />
                          {formatTime(visit.visit_time)}
                        </div>
                      </div>

                      <div className="mt-2 pt-2 border-t border-gray-50 flex justify-between items-center text-xs text-gray-500">
                        <div className="flex items-center">
                          <div className="h-5 w-5 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold mr-2 text-[10px]">
                            {visit.user_name?.charAt(0) || 'U'}
                          </div>
                          {visit.user_name}
                        </div>
                        <div className="flex items-center gap-3">
                          {visit.latitude && (
                            <div className="flex items-center text-blue-600">
                              <MapPin size={12} className="mr-1" />
                              Location Verified
                            </div>
                          )}
                          <button
                            type="button"
                            aria-label={`Nachfass-E-Mail für ${visit.pos_name ?? 'Besuch'}`}
                            title="Nachfass-E-Mail"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEmailVisitId(visit.id);
                            }}
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-teal-700
                              hover:bg-teal-50 transition-colors font-semibold"
                          >
                            <Mail size={14} />
                            <span className="hidden sm:inline">Nachfass-Mail</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
                {visits.length === 0 && (
                  <li className="px-6 py-12 text-center text-gray-500">
                    <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-lg font-medium text-gray-900">No visits recorded yet</p>
                    <p className="mb-6">Start your route and log your first visit.</p>
                    <Link
                      to="/visits/new"
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
                    >
                      <Plus size={20} className="mr-2" />
                      Log Visit
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {hasMore && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="mt-4 w-full min-h-[48px] px-4 py-3 text-blue-600 font-semibold hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Loading...' : `Load ${PAGE_SIZE} more`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Plan Visits Modal */}
      <PlanVisitsModal 
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
      />

      {/* Visit Detail Modal */}
      <VisitDetailModal
        visit={selectedVisit}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedVisit(null);
          if (user?.id) setOpenVisitId(user.id, null);
        }}
      />

      <VisitFollowupEmailModal
        visitId={emailVisitId ?? ''}
        isOpen={!!emailVisitId}
        onClose={() => setEmailVisitId(null)}
      />
    </div>
  );
};

export default Visits;
